// =======================
// infoPerfilEscola.js
// =======================

import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ModalAtos from "./componente/ModalAtos";
import { api } from "@api/api";
import TabelaVinculosInstitucionais from "./componente/TabelaVinculosInstitucionais";
import { coresTextosGlobais, bordaSuperiorModalGlobal, tamanhosControlesGlobais, tamanhosPaginasAdministrativas } from "../../../estilos/global";

const normalizarTexto = (valor = "") =>
  String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function ModalEscola({ open, onClose, escola, onOpenAtos }) {
  const modalidades = Array.isArray(escola?.modalidades)
    ? escola.modalidades
    : escola?.modalidade
      ? [escola.modalidade]
      : [];

  const mantenedoras = Array.isArray(escola?.mantenedoras)
    ? escola.mantenedoras
    : [];

  const formatarResponsabilidades = (mantenedora) => {
    const responsabilidades = Array.isArray(mantenedora?.responsabilidades)
      ? mantenedora.responsabilidades
      : Array.isArray(mantenedora?.etapasModalidades)
        ? mantenedora.etapasModalidades
        : [];

    return responsabilidades
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        return item?.nome || item?.etapa || item?.modalidade || "";
      })
      .filter(Boolean)
      .join(", ");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-detalhes-escola"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
        style={{
          width: "100%",
          maxWidth: 620,
        }}
      >
        <Paper
          sx={{
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            padding: { xs: 2, sm: 2.5 },
            outline: "none",
            border: "1px solid var(--sear-borda, #9fcdff)",
            borderRadius: 2,
            backgroundColor: "var(--sear-superficie, #ffffff)",
            boxShadow: "0px 14px 40px rgba(25, 70, 120, 0.18)",
            ...bordaSuperiorModalGlobal,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                color: "var(--sear-azul, #087cf0)",
                backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
              }}
            >
              <SchoolRoundedIcon />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography
                id="modal-detalhes-escola"
                variant="h6"
                sx={{
                  color: coresTextosGlobais.tituloPagina,
                  fontWeight: 700,
                  fontSize: tamanhosPaginasAdministrativas.tituloPagina,
                  lineHeight: 1.15,
                }}
              >
                {escola?.nome}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "var(--sear-texto-secundario, #7b8798)",
                  fontSize: tamanhosControlesGlobais.descricao,
                  lineHeight: 1.35,
                }}
              >
                Dados institucionais e vínculos
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              aria-label="Fechar detalhes da escola"
              sx={{
                color: "var(--sear-texto-secundario, #3f5184)",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider
            sx={{
              marginTop: 2,
              marginBottom: 1.5,
              borderColor: "var(--sear-borda, #c9def4)",
            }}
          />

          <Typography
            sx={{
              color: "var(--sear-azul, #0767d8)",
              fontWeight: 700,
              marginBottom: 1.5,
            }}
          >
            Dados da Escola
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: "var(--sear-texto-secundario, #5f6f94)" }}>
                Endereço
              </Typography>

              <Typography variant="body2" sx={{ color: "var(--sear-texto, #31415f)" }}>
                {escola?.rua}, {escola?.numero} – {escola?.bairro}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "var(--sear-texto-secundario, #5f6f94)" }}>
                Município
              </Typography>

              <Typography variant="body2" sx={{ color: "var(--sear-texto, #31415f)" }}>
                {escola?.municipio}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "var(--sear-texto-secundario, #5f6f94)" }}>
                CEP
              </Typography>

              <Typography variant="body2" sx={{ color: "var(--sear-texto, #31415f)" }}>
                {escola?.cep}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: "var(--sear-texto-secundario, #5f6f94)" }}>
                Telefone
              </Typography>

              <Typography variant="body2" sx={{ color: "var(--sear-texto, #31415f)" }}>
                {escola?.contato}
              </Typography>
            </Box>

            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <Typography variant="caption" sx={{ color: "var(--sear-texto-secundario, #5f6f94)" }}>
                E-mail
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "var(--sear-texto, #31415f)",
                  wordBreak: "break-word",
                }}
              >
                {escola?.email}
              </Typography>
            </Box>
          </Box>

          <Divider
            sx={{
              marginY: 2,
              borderColor: "var(--sear-borda, #c9def4)",
            }}
          />

          <Typography
            sx={{
              color: "var(--sear-azul, #0767d8)",
              fontWeight: 700,
            }}
          >
            Etapas e modalidades ofertadas
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              marginTop: 1.25,
            }}
          >
            {modalidades.map((modalidade) => (
              <Chip
                key={modalidade}
                label={modalidade}
                size="small"
                variant="outlined"
                sx={{
                  color: "var(--sear-azul, #0767d8)",
                  borderColor: "var(--sear-borda-forte, #087cf0)",
                  backgroundColor: "var(--sear-superficie, #ffffff)",
                }}
              />
            ))}
          </Box>

          <Divider
            sx={{
              marginY: 2,
              borderColor: "var(--sear-borda, #c9def4)",
            }}
          />

          <Typography
            sx={{
              color: "var(--sear-azul, #0767d8)",
              fontWeight: 700,
            }}
          >
            Mantenedoras responsáveis
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 1,
              marginTop: 1.25,
            }}
          >
            {mantenedoras.map((mantenedora) => {
              const responsabilidades = formatarResponsabilidades(mantenedora);

              return (
                <Paper
                  key={mantenedora.id}
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    padding: 1.25,
                    borderColor: "var(--sear-borda, #c9def4)",
                    borderRadius: 1.5,
                    boxShadow: "none",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      color: "var(--sear-azul, #087cf0)",
                      backgroundColor: "var(--sear-superficie-azul, #eaf4ff)",
                    }}
                  >
                    <AccountBalanceRoundedIcon fontSize="small" />
                  </Avatar>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "var(--sear-texto, #31415f)",
                        fontWeight: 600,
                      }}
                    >
                      {mantenedora.nome}
                    </Typography>

                    {responsabilidades && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "var(--sear-texto-secundario, #5f6f94)",
                        }}
                      >
                        Responsável por: {responsabilidades}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              );
            })}

            {mantenedoras.length === 0 && (
              <Typography variant="body2" sx={{ color: "var(--sear-texto-secundario, #5f6f94)" }}>
                Nenhuma mantenedora vinculada.
              </Typography>
            )}
          </Box>

          <Divider
            sx={{
              marginTop: 2,
              marginBottom: 1.5,
              borderColor: "var(--sear-borda, #c9def4)",
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={onOpenAtos}
              sx={{
                minWidth: 125,
                textTransform: "none",
                backgroundColor: "var(--sear-acao-azul, #087cf0)",
                boxShadow: "none",
              }}
            >
              Ver atos
            </Button>
          </Box>
        </Paper>
      </motion.div>
    </Modal>
  );
}

export default function ListaEscolas({ ativo = true, busca = "" } = {}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAtosOpen, setModalAtosOpen] = useState(false);

  const [selectedEscola, setSelectedEscola] = useState(null);
  const [escolas, setEscolas] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(api.listUserSchools(), {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && Array.isArray(data.escolas)) {
          const agrupadas = Object.values(
            data.escolas.reduce((acc, escola) => {
              const chave = escola.id_procura_atos || escola.id;

              const modalidadesRecebidas = Array.isArray(escola.modalidades)
                ? escola.modalidades
                : escola.modalidade
                  ? [escola.modalidade]
                  : [];

              const mantenedorasRecebidas = Array.isArray(escola.mantenedoras)
                ? escola.mantenedoras
                : escola.mantenedora_id
                  ? [
                      {
                        id: escola.mantenedora_id,
                        nome: escola.nome_mantenedora,
                      },
                    ]
                  : [];

              if (!acc[chave]) {
                acc[chave] = {
                  ...escola,
                  modalidades: [...modalidadesRecebidas],
                  mantenedoras: [...mantenedorasRecebidas],
                };

                return acc;
              }

              modalidadesRecebidas.forEach((modalidade) => {
                if (!acc[chave].modalidades.includes(modalidade)) {
                  acc[chave].modalidades.push(modalidade);
                }
              });

              mantenedorasRecebidas.forEach((mantenedora) => {
                const jaExiste = acc[chave].mantenedoras.some(
                  (item) => String(item.id) === String(mantenedora.id),
                );

                if (!jaExiste) {
                  acc[chave].mantenedoras.push(mantenedora);
                }
              });

              return acc;
            }, {}),
          );

          setEscolas(agrupadas);
        } else {
          console.error("Formato inesperado da resposta:", data);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar escolas:", error);
      });
  }, []);

  const handleOpenModal = (escola) => {
    setSelectedEscola(escola);
    setModalOpen(true);
  };

  const handleOpenAtos = (escola) => {
    setSelectedEscola(escola);
    setModalAtosOpen(true);
  };

  const escolasFiltradas = escolas.filter((escola) => {
    const termo = normalizarTexto(busca);

    return (
      normalizarTexto(escola.nome).includes(termo) ||
      normalizarTexto(escola.municipio).includes(termo) ||
      escola.modalidades.some((modalidade) =>
        normalizarTexto(modalidade).includes(termo),
      )
    );
  });

  return (
    <>
      <TabelaVinculosInstitucionais
        tipo="escolas"
        ativo={ativo}
        itens={escolasFiltradas}
        onDetalhes={handleOpenModal}
        onAtos={handleOpenAtos}
      />

      <ModalEscola
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        escola={selectedEscola}
        onOpenAtos={() => {
          setModalOpen(false);
          setModalAtosOpen(true);
        }}
      />

      <ModalAtos
        open={modalAtosOpen}
        onClose={() => setModalAtosOpen(false)}
        escola={selectedEscola}
      />
    </>
  );
}
